
import React, { useState, useEffect } from 'react';
import { User as UserIcon, Lock, X, Mail, Globe2, KeyRound, Eye, EyeOff } from 'lucide-react';
import { storage, createInitialTournamentData, migrateUserData, fetchManagedTournamentMasterLists, fetchManagedDrawPresetDecks } from '../storage';
import { normalizeCko2026WarmupProfile } from '../cko2026Warmup';
import { isLocalBackend, phpApiUrl, setAuthToken } from '../apiEnv';
import { User } from '../types';

export const AUTH_REMEMBER_ACCOUNT_KEY = 'kendama_remembered_account';
export const AUTH_AUTO_LOGIN_KEY = 'kendama_auto_login';

// 登录响应里有些字段是「JSON 字符串」（PHP 侧 meta 的序列化结果）。
// 直接 JSON.parse 遇到脏数据会抛错、连带整次登录失败，这里统一兜底成默认值。
const parseUserJsonField = <T,>(value: unknown, fallback: T): T => {
  if (typeof value !== 'string') {
    return (value === undefined || value === null ? fallback : value as T);
  }
  try {
    const parsed = JSON.parse(value);
    return (parsed === undefined || parsed === null ? fallback : parsed as T);
  } catch {
    return fallback;
  }
};

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLogin: (user: User) => void;
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, onLogin }) => {
  const [isRegistering, setIsRegistering] = useState(false);
  const [isRecoveringPassword, setIsRecoveringPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState(''); 
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [invitationCode, setInvitationCode] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [rememberAccount, setRememberAccount] = useState(false);
  const [autoLogin, setAutoLogin] = useState(false);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [countdown]);

  // 打开登录框时，恢复记住的账号与自动登录选项
  useEffect(() => {
    if (!isOpen) return;
    try {
      const autoLoginRaw = window.localStorage.getItem(AUTH_AUTO_LOGIN_KEY);
      if (autoLoginRaw) {
        const cred = JSON.parse(autoLoginRaw);
        if (cred && cred.email) {
          setEmail(cred.email);
          setRememberAccount(true);
          setAutoLogin(true);
          return;
        }
      }
      const remembered = window.localStorage.getItem(AUTH_REMEMBER_ACCOUNT_KEY);
      if (remembered) {
        setEmail(remembered);
        setRememberAccount(true);
      }
    } catch (e) {
      // 忽略本地存储读取异常
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSendCode = async () => {
    if (!email) {
      setError('Please enter your email first / 请先输入邮箱');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Invalid email format / 邮箱格式无效');
      return;
    }

    setError('');
    setInfo('');
    setLoading(true);

    try {
      const endpoint = isLocalBackend()
        ? (isRecoveringPassword ? '/api/send-reset-code' : '/api/send-code')
        : phpApiUrl(isRecoveringPassword ? 'send_reset_code' : 'send_code');
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (data.success) {
        setInfo(
          data.message ||
            (isRecoveringPassword
              ? 'Password reset code sent! / 重置密码验证码已发送！'
              : 'Verification code sent! / 验证码已发送！')
        );
        setCountdown(60);
      } else {
        throw new Error(data.error || 'Failed to send code / 发送验证码失败');
      }
    } catch (err: any) {
      console.error("Send Code Error:", err);
      setError(err.message || 'Network error / 网络错误');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);

    // Basic validation
    if (isRegistering && (!email || !password || !confirmPassword || !username || !verificationCode)) {
      setError('Please fill in all fields including verification code / 请填写所有字段和验证码');
      setLoading(false);
      return;
    }
    if (isRecoveringPassword && (!email || !password || !confirmPassword || !verificationCode)) {
      setError('Please fill in email, verification code and new password / 请填写邮箱、验证码和新密码');
      setLoading(false);
      return;
    }
    if (isRegistering && password !== confirmPassword) {
      setError('Passwords do not match / 两次输入的密码不一致');
      setLoading(false);
      return;
    }
    if (isRecoveringPassword && password !== confirmPassword) {
      setError('Passwords do not match / 两次输入的密码不一致');
      setLoading(false);
      return;
    }
    if (isRegistering || isRecoveringPassword) {
      const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d]{8,}$/;
      if (!passwordRegex.test(password)) {
        setError('Password must contain at least one uppercase letter, one lowercase letter, and one number (min 8 chars) / 密码必须包含至少一个大写字母、一个小写字母和一个数字（最少8位）');
        setLoading(false);
        return;
      }
    }
    if (!isRegistering && !isRecoveringPassword && (!email || !password)) {
      setError('Please fill in email and password / 请填写登录邮箱和密码');
      setLoading(false);
      return;
    }

    try {
      const endpoint = isLocalBackend()
        ? (isRegistering ? '/api/register' : isRecoveringPassword ? '/api/reset-password' : '/api/login')
        : phpApiUrl(isRegistering ? 'register' : isRecoveringPassword ? 'reset_password' : 'login');
      
      const body = isRegistering
        ? { email, password, displayName: username, username, code: verificationCode, invitationCode }
        : isRecoveringPassword
          ? { email, password, code: verificationCode }
          : { email, password, username: email };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const raw = await response.text();
      let data: any = null;
      if (raw) {
        try {
          data = JSON.parse(raw);
        } catch {
          throw new Error(
            `Non-JSON response (${response.status}). / 服务器返回非JSON（${response.status}）。`
          );
        }
      } else {
        data = {};
      }

      if (data.success) {
        if (isRegistering) {
          setInfo('Registration successful! Please login. / 注册成功！请直接登录。');
          setIsRegistering(false);
          setPassword('');
          setConfirmPassword('');
          setVerificationCode('');
        } else if (isRecoveringPassword) {
          setInfo('Password reset successful! Please login with your new password. / 密码重置成功！请使用新密码登录。');
          setIsRecoveringPassword(false);
          setPassword('');
          setConfirmPassword('');
          setVerificationCode('');
        } else {
          // Map PHP response user data to our User type
          const user: User = {
            uid: data.user.uid || data.user.ID,
            email: data.user.email || data.user.user_email,
            username: data.user.username || data.user.user_login || data.user.email,
            displayName: data.user.displayName || data.user.username || data.user.email,
            customDecks: parseUserJsonField(data.user.customDecks, []),
            standardDeckOverrides: parseUserJsonField(data.user.standardDeckOverrides, undefined),
            tournamentData: parseUserJsonField(data.user.tournamentData, createInitialTournamentData()),
            tournamentHistory: parseUserJsonField(data.user.tournamentHistory, []),
            matchHistory: parseUserJsonField(data.user.matchHistory, []),
            // 签到数据随用户对象一起返回；漏掉这个字段会让已签到记录被当成空数据清掉
            dailyCheckIn: parseUserJsonField(data.user.dailyCheckIn, undefined),
            cko2026Warmup: normalizeCko2026WarmupProfile(
              parseUserJsonField(data.user.cko2026Warmup, undefined)
            ),
            isAdmin: data.user.isAdmin === true || data.user.isAdmin === 'true' || data.user.isAdmin === 1 || data.user.isAdmin === '1',
            roles: Array.isArray(data.user.roles) ? data.user.roles : [],
            canUploadFinalConfig:
              data.user.canUploadFinalConfig === true ||
              data.user.canUploadFinalConfig === 'true' ||
              data.user.canUploadFinalConfig === 1 ||
              data.user.canUploadFinalConfig === '1'
          };
          
          const managedMasterLists = await fetchManagedTournamentMasterLists();
          const managedDrawPresetDecks = await fetchManagedDrawPresetDecks();

          // 根据勾选状态保存或清除本地登录凭据
          try {
            if (rememberAccount || autoLogin) {
              window.localStorage.setItem(AUTH_REMEMBER_ACCOUNT_KEY, email);
            } else {
              window.localStorage.removeItem(AUTH_REMEMBER_ACCOUNT_KEY);
            }
            if (autoLogin) {
              window.localStorage.setItem(AUTH_AUTO_LOGIN_KEY, JSON.stringify({ email, password }));
            } else {
              window.localStorage.removeItem(AUTH_AUTO_LOGIN_KEY);
            }
          } catch (e) {
            // 忽略本地存储写入异常
          }

          // 保存服务端签发的跨域 token（供 X-Kendama-Auth 请求头使用）
          if (data.token) {
            setAuthToken(data.token);
          }

          onLogin(migrateUserData(user, managedMasterLists, managedDrawPresetDecks));
          onClose();
        }
      } else {
        const msg =
          data?.error ||
          (!response.ok
            ? `Server error (${response.status}). / 服务器错误（${response.status}）。`
            : 'Authentication failed / 验证失败');
        throw new Error(msg);
      }
    } catch (err: any) {
      console.error("Auth Error:", err);
      setError(err.message || 'Server connection error / 服务器连接错误');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120] overflow-y-auto bg-black/90 backdrop-blur-md px-2 py-3 sm:p-4 animate-in fade-in duration-300">
      <div className="flex min-h-full w-full items-center justify-center">
        <div className="relative w-full max-w-md max-h-[calc(100dvh-1.5rem)] sm:max-h-[calc(100dvh-2rem)] overflow-y-auto bg-[#0a0a0a] border border-blue-500/30 rounded-2xl px-4 py-5 sm:p-8 shadow-[0_0_50px_rgba(59,130,246,0.15)]">
        <button onClick={onClose} className="absolute top-3 right-3 sm:top-6 sm:right-6 text-zinc-400 hover:text-white transition-colors z-10 bg-black/60 rounded-lg p-1">
          <X size={22} />
        </button>

        <div className="flex flex-col items-center mb-5 sm:mb-8">
           <h2 className="text-2xl sm:text-3xl font-orbitron font-black text-white tracking-widest text-center uppercase">
             {isRegistering ? 'Initialize / 注册' : isRecoveringPassword ? 'Recover / 找回密码' : 'Connect / 登录'}
           </h2>
           <p className="text-zinc-500 font-rajdhani text-[11px] sm:text-sm uppercase tracking-[0.2em] mt-1">
             Login
           </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3.5 sm:space-y-5">
          {(isRegistering || isRecoveringPassword) && (
            <div className="space-y-1 animate-in slide-in-from-top-2">
              <label className="text-[10px] font-orbitron text-zinc-400 uppercase tracking-widest pl-1">Email / 邮箱</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" size={16} />
                <input 
                  type="email" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-black/50 border border-zinc-800 rounded-xl py-2.5 sm:py-3 pl-10 pr-20 sm:pr-24 text-base text-white focus:border-blue-500 focus:outline-none transition-all font-rajdhani"
                  placeholder="agent@example.com"
                />
                <button
                  type="button"
                  onClick={handleSendCode}
                  disabled={loading || countdown > 0}
                  className={`absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 rounded-lg text-[9px] sm:text-[10px] font-orbitron tracking-wider transition-colors
                    ${(loading || countdown > 0) 
                      ? 'bg-zinc-800 text-zinc-500 cursor-not-allowed' 
                      : 'bg-blue-600/20 text-blue-400 hover:bg-blue-600/40 border border-blue-500/30'}`}
                >
                  {countdown > 0 ? `${countdown}s` : (isRecoveringPassword ? 'RESET CODE' : 'SEND CODE')}
                </button>
              </div>
            </div>
          )}

          {(isRegistering || isRecoveringPassword) && (
            <div className="space-y-1 animate-in slide-in-from-top-2">
              <label className="text-[10px] font-orbitron text-zinc-400 uppercase tracking-widest pl-1">
                {isRecoveringPassword ? 'Reset Code / 重置验证码' : 'Verification Code / 验证码'}
              </label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" size={16} />
                <input 
                  type="text" 
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  className="w-full bg-black/50 border border-zinc-800 rounded-xl py-2.5 sm:py-3 pl-10 pr-4 text-base text-white focus:border-blue-500 focus:outline-none transition-all font-rajdhani"
                  placeholder="123456"
                />
              </div>
            </div>
          )}

          {/* 
          {isRegistering && (
            <div className="space-y-1.5 animate-in slide-in-from-top-2">
              <label className="text-[10px] font-orbitron text-zinc-400 uppercase tracking-widest pl-1">Invitation Code / 邀请码</label>
              <div className="relative">
                <Globe2 className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" size={18} />
                <input 
                  type="text" 
                  value={invitationCode}
                  onChange={(e) => setInvitationCode(e.target.value)}
                  className="w-full bg-black/50 border border-zinc-800 rounded-xl py-3 pl-11 pr-4 text-white focus:border-blue-500 focus:outline-none transition-all font-rajdhani"
                  placeholder="Enter invitation code..."
                />
              </div>
            </div>
          )}
          */}

          {!isRegistering && (
            <div className="space-y-1 animate-in slide-in-from-top-2">
              <label className="text-[10px] font-orbitron text-zinc-400 uppercase tracking-widest pl-1">Email / 邮箱</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" size={16} />
                <input 
                  type="email" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-black/50 border border-zinc-800 rounded-xl py-2.5 sm:py-3 pl-10 pr-4 text-base text-white focus:border-blue-500 focus:outline-none transition-all font-rajdhani"
                  placeholder="agent@example.com"
                />
              </div>
            </div>
          )}

          {isRegistering && (
            <div className="space-y-1 animate-in slide-in-from-top-2">
              <label className="text-[10px] font-orbitron text-zinc-400 uppercase tracking-widest pl-1">Nickname / 昵称</label>
              <div className="relative">
                <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" size={16} />
                <input 
                  type="text" 
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full bg-black/50 border border-zinc-800 rounded-xl py-2.5 sm:py-3 pl-10 pr-4 text-base text-white focus:border-blue-500 focus:outline-none transition-all font-rajdhani"
                  placeholder="Enter nickname..."
                />
              </div>
            </div>
          )}

          <div className="space-y-1">
            <label className="text-[10px] font-orbitron text-zinc-400 uppercase tracking-widest pl-1">Access Key / 登录密码</label>
            {isRecoveringPassword && (
              <p className="text-[10px] text-zinc-500 font-rajdhani pl-1 mt-1 leading-snug">
                New password / 新密码
              </p>
            )}
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" size={16} />
              <input 
                type={showPassword ? "text" : "password"} 
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-black/50 border border-zinc-800 rounded-xl py-2.5 sm:py-3 pl-10 pr-11 text-base text-white focus:border-blue-500 focus:outline-none transition-all font-rajdhani"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {(isRegistering || isRecoveringPassword) && (
              <p className="text-[10px] text-zinc-500 font-rajdhani pl-1 mt-1 leading-snug">
                Min 8 chars, 1 uppercase, 1 lowercase, 1 number / 至少8位，包含大小写字母与数字
              </p>
            )}
          </div>

          {(isRegistering || isRecoveringPassword) && (
            <div className="space-y-1 animate-in slide-in-from-top-2">
              <label className="text-[10px] font-orbitron text-zinc-400 uppercase tracking-widest pl-1">
                {isRecoveringPassword ? 'Confirm New Password / 确认新密码' : 'Confirm Access Key / 确认密码'}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" size={16} />
                <input 
                  type={showConfirmPassword ? "text" : "password"} 
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full bg-black/50 border border-zinc-800 rounded-xl py-2.5 sm:py-3 pl-10 pr-11 text-base text-white focus:border-blue-500 focus:outline-none transition-all font-rajdhani"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
                >
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
          )}

          {!isRegistering && !isRecoveringPassword && (
            <div className="flex items-center justify-between gap-3 px-1">
              <label className="flex cursor-pointer select-none items-center gap-2">
                <input
                  type="checkbox"
                  checked={rememberAccount}
                  onChange={(e) => {
                    setRememberAccount(e.target.checked);
                    if (!e.target.checked) setAutoLogin(false);
                  }}
                  className="h-4 w-4 rounded border-zinc-700 bg-black/50 accent-cyan-400"
                />
                <span className="text-xs font-rajdhani text-zinc-300">记住账号</span>
              </label>
              <label className="flex cursor-pointer select-none items-center gap-2">
                <input
                  type="checkbox"
                  checked={autoLogin}
                  onChange={(e) => {
                    setAutoLogin(e.target.checked);
                    if (e.target.checked) setRememberAccount(true);
                  }}
                  className="h-4 w-4 rounded border-zinc-700 bg-black/50 accent-cyan-400"
                />
                <span className="text-xs font-rajdhani text-zinc-300">自动登录</span>
              </label>
            </div>
          )}

          {info && <div className="text-emerald-400 text-xs font-rajdhani text-center bg-emerald-950/20 py-2 rounded-lg border border-emerald-500/30">{info}</div>}
          {error && <div className="text-rose-500 text-xs font-rajdhani text-center bg-rose-950/20 py-2 rounded-lg border border-rose-500/30">{error}</div>}

          <button 
            type="submit" 
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-500 text-white font-orbitron font-bold py-3 sm:py-4 rounded-xl transition-all shadow-[0_0_20px_rgba(37,99,235,0.3)] hover:shadow-[0_0_30px_rgba(37,99,235,0.5)] tracking-widest disabled:opacity-50 disabled:cursor-not-allowed text-[11px] sm:text-base"
          >
            {loading
              ? 'PROCESSING... / 处理中...'
              : (isRegistering
                ? 'EXECUTE REGISTRATION / 执行注册'
                : isRecoveringPassword
                  ? 'RESET PASSWORD / 重置密码'
                  : 'ESTABLISH LINK / 建立连接')}
          </button>
        </form>

        {!isRegistering && !isRecoveringPassword && (
          <div className="mt-3 text-center">
            <button
              onClick={() => {
                setIsRecoveringPassword(true);
                setError('');
                setInfo('');
                setVerificationCode('');
                setPassword('');
                setConfirmPassword('');
              }}
              className="text-cyan-400 hover:text-cyan-300 text-xs sm:text-sm font-orbitron font-bold tracking-[0.16em] uppercase transition-colors"
            >
              Forgot Password? / 忘记密码
            </button>
          </div>
        )}

        <div className="mt-4 sm:mt-8 text-center bg-blue-900/10 border border-blue-500/20 rounded-xl p-2.5 sm:p-3 shadow-[0_0_10px_rgba(59,130,246,0.1)]">
          <button 
            onClick={() => {
                setIsRegistering(!isRegistering);
                setIsRecoveringPassword(false);
                setError('');
                setInfo('');
                setVerificationCode('');
                setPassword('');
                setConfirmPassword('');
            }}
            className="text-blue-400 hover:text-blue-300 text-xs sm:text-sm font-orbitron font-bold tracking-widest uppercase transition-colors"
          >
            {isRegistering ? 'Already Registered? Login / 已有账号？登录' : 'New User? Register Now / 新用户？立即注册'}
          </button>
        </div>

        {isRecoveringPassword && (
          <div className="mt-3 text-center">
            <button
              onClick={() => {
                setIsRecoveringPassword(false);
                setError('');
                setInfo('');
                setVerificationCode('');
                setPassword('');
                setConfirmPassword('');
              }}
              className="text-zinc-400 hover:text-zinc-200 text-xs sm:text-sm font-orbitron font-bold tracking-[0.16em] uppercase transition-colors"
            >
              Back To Login / 返回登录
            </button>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default AuthModal;
